<script>
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { onBeforeUnmount, onMounted, reactive, toRefs, watch } from "vue";

export default {
    name: "PdfPreviewModal",
    props: {
        report: {
            type: Object,
            default: null,
        },
        visible: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["close"],
    setup(props, { emit }) {
        GlobalWorkerOptions.workerSrc = "/js/pdf.worker.min.js";
        const state = reactive({
            pages: [],
            loading: false,
            error: false,
        });

        const close = () => emit("close");
        const handleEscape = (event) => {
            if (event.key === "Escape" && props.visible) close();
        };
        const loadPdf = async (url) => {
            state.pages = [];
            state.error = false;
            state.loading = true;
            try {
                const pdf = await getDocument(url).promise;
                for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
                    const page = await pdf.getPage(pageNumber);
                    const viewport = page.getViewport({ scale: 1.35 });
                    const canvas = document.createElement("canvas");
                    const context = canvas.getContext("2d");

                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    await page.render({ canvasContext: context, viewport }).promise;
                    state.pages.push({
                        number: pageNumber,
                        image: canvas.toDataURL("image/jpeg", 0.9),
                    });
                }
            } catch (error) {
                console.error("Gagal memuat preview PDF", error);
                state.error = true;
            } finally {
                state.loading = false;
            }
        };

        watch(
            () => [props.visible, props.report && props.report.link],
            ([visible, url]) => {
                if (visible && url) loadPdf(url);
            }
        );
        onMounted(() => document.addEventListener("keydown", handleEscape));
        onBeforeUnmount(() => document.removeEventListener("keydown", handleEscape));

        return { ...toRefs(state), close };
    },
};
</script>

<template>
    <div
        v-if="visible && report"
        class="pdf-preview-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="`Preview ${report.name}`"
        @click.self="close"
    >
        <div class="pdf-preview-dialog">
            <div class="pdf-preview-content">
                <header class="pdf-preview-header">
                    <div>
                        <p class="pdf-preview-eyebrow">Preview dokumen</p>
                        <h2 class="pdf-preview-title">{{ report.name }}</h2>
                    </div>
                    <div class="pdf-preview-actions">
                        <a
                            :href="report.link"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="pdf-preview-open"
                        >
                            Buka di tab baru
                        </a>
                        <button
                            type="button"
                            class="pdf-preview-close"
                            aria-label="Tutup preview PDF"
                            @click="close"
                        >
                            &times;
                        </button>
                    </div>
                </header>
                <div class="pdf-preview-body">
                    <div v-if="loading" class="pdf-preview-state">
                        <div class="pdf-spinner"></div>
                        <p>Memuat preview PDF...</p>
                    </div>
                    <div v-else-if="error" class="pdf-preview-state">
                        <p>Preview PDF tidak dapat dimuat.</p>
                        <a :href="report.link" target="_blank" rel="noopener noreferrer" class="pdf-preview-open">
                            Buka dokumen
                        </a>
                    </div>
                    <div v-else class="pdf-pages">
                        <div v-for="page in pages" :key="page.number" class="pdf-page">
                            <img :src="page.image" :alt="`Halaman ${page.number}`" />
                            <span>Halaman {{ page.number }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.pdf-preview-modal { position: fixed; inset: 0; z-index: 9999; display: flex; align-items: center; justify-content: center; width: 100vw; height: 100vh; overflow: hidden; background: rgba(15, 23, 42, .78); }
.pdf-preview-dialog { width: min(94vw, 1180px); height: min(92vh, 900px); }
.pdf-preview-content { display: flex; flex-direction: column; height: 100%; overflow: hidden; border-radius: 14px; background: #f1f5f9; box-shadow: 0 24px 80px rgba(0, 0, 0, .35); }
.pdf-preview-header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1rem 1.25rem; color: #e2e8f0; background: #172554; }
.pdf-preview-eyebrow { margin: 0 0 .2rem; color: #93c5fd; font-size: .72rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
.pdf-preview-title { margin: 0; color: #fff; font-size: 1.05rem; font-weight: 700; }
.pdf-preview-actions { display: flex; align-items: center; gap: .65rem; }
.pdf-preview-open, .pdf-preview-close { display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: 8px; cursor: pointer; font-size: .85rem; font-weight: 600; text-decoration: none; }
.pdf-preview-open { padding: .6rem .85rem; color: #172554; background: #bfdbfe; }
.pdf-preview-close { width: 2.2rem; height: 2.2rem; color: #fff; background: rgba(255,255,255,.14); font-size: 1.5rem; line-height: 1; }
.pdf-preview-body { flex: 1; min-height: 0; overflow: auto; padding: 1.25rem; background: #cbd5e1; }
.pdf-pages { display: flex; flex-direction: column; align-items: center; gap: 1.25rem; }
.pdf-page { width: min(100%, 820px); padding: .5rem; border-radius: 4px; background: #fff; box-shadow: 0 8px 20px rgba(15, 23, 42, .2); }
.pdf-page img { display: block; width: 100%; height: auto; }
.pdf-page span { display: block; padding-top: .35rem; color: #64748b; font-size: .75rem; text-align: center; }
.pdf-preview-state { display: flex; min-height: 240px; flex-direction: column; align-items: center; justify-content: center; gap: .75rem; color: #475569; }
.pdf-spinner { width: 2rem; height: 2rem; border: 3px solid #cbd5e1; border-top-color: #2563eb; border-radius: 9999px; animation: pdf-spin .8s linear infinite; }
@keyframes pdf-spin { to { transform: rotate(360deg); } }
@media (max-width: 640px) { .pdf-preview-dialog { width: 100vw; height: 100vh; } .pdf-preview-content { border-radius: 0; } .pdf-preview-title { max-width: 45vw; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } .pdf-preview-open { padding: .55rem; font-size: 0; } .pdf-preview-open::after { content: "↗"; font-size: 1rem; } }
</style>
